<template>
  <v-app-bar
    id="core-app-bar"
    absolute
    app
    color="transparent"
    flat
    height="88"
  > 
    <v-toolbar-title class="tertiary--text font-weight-light align-self-center">
      <v-btn
        v-if="responsive"
        color="primary"
        icon
        @click.stop="onClickBtn"
      >
        <v-icon>mdi-view-list</v-icon>
      </v-btn>
      {{ title }}
    </v-toolbar-title>

    <v-spacer />

    <v-toolbar-items>
      <v-row
        align="center"
        class="mx-0"
      >
        <v-menu
          v-if="!responsive"
          bottom
          left
          offset-y
          transition="slide-y-transition"
        >
          <template v-slot:activator="{ attrs, on }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              @click.native="markAllAsRead" 
            >
              <v-badge
                v-model="show"
                color="error"
                overlap
              >
                <template slot="badge">
                  {{ badges }}
                </template>
                <v-icon
                  size="21px"
                  color="tertiary"
                >
                  mdi-bell
                </v-icon>
              </v-badge>
            </v-btn>
          </template>

          <v-card max-height="250">
            <v-list
              v-if="notifications.length > 0"
              dense
            >
              <v-list-item
                v-for="notification in notifications"
                :key="notification.id"
                @click="onClick(notification.data.page)"
              >
                <v-list-item-content>
                  <v-list-item-title
                    v-html="notification.data.message"
                  />
                  <v-list-item-subtitle
                    class="caption"
                    v-text="timeAgo(notification.created_at)"
                  />
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-list v-else>
              <v-list-item-content>
                <v-list-item-subtitle
                  class="caption mx-4"
                >
                  You are currently up to date. No new notifications have been received.
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list>
          </v-card>
        </v-menu>

        <v-tooltip
          v-if="!responsive"
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              to="/profile"
              v-on="on"
            >
              <v-icon
                size="21px"
                color="tertiary"
              >
                mdi-account
              </v-icon>
            </v-btn>
          </template>
          <span>View Profile</span>
        </v-tooltip>

        <v-tooltip
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-btn 
              icon
              :href="workspaceLink"
              target="_blank"
              v-on="on"        
            >
              <v-icon
                size="20px"
                color="tertiary"
              >
                mdi-link-variant
              </v-icon>
            </v-btn>
          </template>
          <span>Workspace (HRM)</span>
        </v-tooltip>

        <v-menu 
          offset-y
          open-on-hover
        >
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              v-on="on"
            >
              <v-icon
                color="tertiary" 
                size="21px"      
              >
                mdi-cog
              </v-icon>  
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              @click="logout"
            >
              <v-list-item-action>
                <v-icon>mdi-power</v-icon>
              </v-list-item-action>
              <v-list-item-title>Logout</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$refs.dialog.setDialog(true)"> 
              <v-list-item-action>
                <v-icon>mdi-account-key</v-icon>
              </v-list-item-action>
              <v-list-item-title> Change Password </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <user-password-dialog-edit
          ref="dialog"
          :user="user"
        />
      </v-row>
    </v-toolbar-items>
  </v-app-bar>
</template>

<script>
  import notificationPaths from '@/router/notification-paths'
  import {
    mapMutations, mapState,
  } from 'vuex'

  export default {
    components: {
      UserPasswordDialogEdit: () => import('@/components/hris/user/password/DialogEdit'),
    },

    data: () => ({
      notifications: [],
      unreadNotifications: [],
      show: false,
      title: null,
      responsive: false,
    }),

    computed: {
      ...mapState({ user: 'currentUser' }),
      badges () {
        return this.unreadNotifications.length
      },
      workspaceLink () {
        switch (process.env.VUE_APP_ENV) {
          case 'local':
            return 'http://localhost:8081'
          case 'staging':
            return 'https://hrmtraining.trinitysearch.net'
          case 'production':
            return 'https://hrm.trinitysearch.net'
          default:
            return 'https://hrm.trinitysearch.net'
        }
      },
    },
    watch: {
      $route (to, from) {
        this.title = to.name

        if (to.path !== '/leave-dashboard' && this.$vuetify.breakpoint.mdAndUp) {
          this.responsive = false
        }
      },
      badges (val) {
        this.show = val 
      },
    },
    created () {
      this.loadNotifications()
    },
    mounted () {
      this.onResponsiveInverted()
      window.addEventListener('resize', this.onResponsiveInverted)
    },
    beforeDestroy () {
      window.removeEventListener('resize', this.onResponsiveInverted)
    },

    methods: {
      ...mapMutations('app', ['setDrawer']),
      onClickBtn () {
        this.setDrawer(!this.$store.state.app.drawer)
      },
      onClick (page) {
        this.$router.push({ path: notificationPaths[page] })
      },
      markAllAsRead () {
        if (this.badges === 0) return

        this.$api.notifications().update()
          .then(response => {
            this.loadNotifications()
          })
      },
      loadNotifications () {
        this.$api.notifications().list()
          .then(response => {
            this.notifications = response.data
            this.unreadNotifications = this.notifications.filter(notification => notification.read_at === null)
          })
      },
      timeAgo (timestamp) {
        return this.$moment(timestamp).fromNow()
      },
      onResponsiveInverted () {
        if (this.$vuetify.breakpoint.smAndDown || this.$route.path === '/leave-dashboard') {
          this.responsive = true
        } else {
          this.responsive = false
        }
      },
      logout () {
        this.$logout()
          .then((response) => {
            this.$router.push({ path: '/login' })        
          })
      },
    },
  }
</script>

<style>
  /* Fix coming in v2.0.8 */
  #core-app-bar {
    width: auto;
  }

  #core-app-bar a {
    text-decoration: none;
  }
</style>
